/**
 * Custom TopbarDesktop for ConstructionYardSale.com
 * Ported from Replit design navbar.tsx: exact same tailwind classes.
 * Same props interface as the default TopbarDesktop.
 */
import React, { useState, useEffect } from 'react';

import { FormattedMessage } from '../../../../util/reactIntl';
import { Avatar, LinkedLogo, NamedLink } from '../../../../components';

import { Input } from '../../../../components/ui/input';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';
import NotificationBell from '../../../../components/NotificationBell/NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import { Button } from '../../../../components/ui/button';

import { Search, LayoutDashboard, MessageSquare, User, Settings, LogOut } from 'lucide-react';

const TopbarDesktopCustom = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
  } = props;

  const [mounted, setMounted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    setMounted(true);
  }, []);

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const displayName =
    currentUser?.attributes?.profile?.displayName ||
    currentUser?.attributes?.profile?.firstName ||
    '';
  const email = currentUser?.attributes?.email || '';

  const handleSearch = e => {
    e.preventDefault();
    onSearchSubmit({ keywords: searchQuery.trim() });
  };

  // Replit: <div className="hidden md:flex flex-grow max-w-md lg:max-w-xl mx-4 lg:mx-8">
  //           <form className="relative w-full">
  //             <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
  //             <Input className="pl-9 h-10 w-full bg-muted/50 border-muted-foreground/20 focus-visible:ring-1" />
  const searchFormMaybe = showSearchForm ? (
    <div className="hidden md:flex mx-4 lg:mx-8">
      <form onSubmit={handleSearch} className="relative w-64 lg:w-80">
        <button type="submit" className="absolute left-3 top-1/2 -translate-y-1/2 p-0 bg-transparent border-none cursor-pointer">
          <Search className="h-4 w-4 text-muted-foreground" />
        </button>
        <Input
          type="text"
          placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
          className="pl-9 h-10 w-full bg-muted/50 border-muted-foreground/20 focus-visible:ring-1"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </form>
    </div>
  ) : null;

  // Replit: <Button variant="ghost" size="icon" className="relative h-10 w-10 rounded-full text-muted-foreground hover:text-primary">
  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <NamedLink
      name="InboxPage"
      params={{}}
      className="relative inline-flex items-center justify-center h-10 w-10 rounded-full text-muted-foreground hover:text-primary transition-colors"
    >
      <MessageSquare className="h-5 w-5" strokeWidth={2.25} />
      {notificationCount > 0 && (
        <span className="absolute top-2 right-2 h-2 w-2 bg-primary rounded-full" />
      )}
    </NamedLink>
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative ml-2 cursor-pointer rounded-full">
          <Avatar user={currentUser} disableProfileLink />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{displayName}</p>
            {email && <p className="text-xs leading-none text-muted-foreground">{email}</p>}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {showCreateListingsLink && (
          <DropdownMenuItem asChild className="cursor-pointer">
            <NamedLink
              name="DashboardPage"
              style={
                currentPage === 'DashboardPage'
                  ? { boxShadow: 'inset 4px 0 0 0 hsl(var(--primary))' }
                  : undefined
              }
              className="flex items-center w-full text-foreground no-underline hover:text-foreground"
            >
              <LayoutDashboard className="mr-2 h-4 w-4 text-foreground" />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild className="cursor-pointer">
          <NamedLink
            name="InboxPage"
            params={{}}
            style={
              currentPage === 'InboxPage'
                ? { boxShadow: 'inset 4px 0 0 0 hsl(var(--primary))' }
                : undefined
            }
            className="flex items-center w-full text-foreground no-underline hover:text-foreground"
          >
            <MessageSquare className="mr-2 h-4 w-4 text-foreground" />
            <FormattedMessage id="TopbarDesktop.inbox" />
          </NamedLink>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="cursor-pointer">
          <NamedLink
            name="ProfileSettingsPage"
            params={{ tab: 'general' }}
            style={
              currentPage === 'ProfileSettingsPage'
                ? { boxShadow: 'inset 4px 0 0 0 hsl(var(--primary))' }
                : undefined
            }
            className="flex items-center w-full text-foreground no-underline hover:text-foreground"
          >
            <Settings className="mr-2 h-4 w-4 text-foreground" />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onLogout}
          className="cursor-pointer text-foreground focus:text-foreground"
        >
          <LogOut className="mr-2 h-4 w-4 text-foreground" />
          <FormattedMessage id="TopbarDesktop.logout" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;

  // Replit: <Button variant="outline" className="font-heading uppercase tracking-wide">Log In</Button>
  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="SignupPage">
      <Button className="font-heading uppercase tracking-wide">
        <FormattedMessage id="TopbarDesktop.signup" />
      </Button>
    </NamedLink>
  );

  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage">
      <Button variant="outline" className="font-heading uppercase tracking-wide">
        <FormattedMessage id="TopbarDesktop.login" />
      </Button>
    </NamedLink>
  );

  // Replit: <div className="container mx-auto px-4 h-16 flex items-center justify-between">
  return (
    <div
      className="max-w-screen-2xl mx-auto px-4 h-16 flex items-center justify-between"
      role="navigation"
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      <div className="flex items-center gap-2 flex-shrink-0">
        <LinkedLogo
          id="logo-topbar-desktop"
          rootClassName="flex items-center flex-shrink-0"
          logoClassName="flex items-center"
          logoImageClassName="h-12 w-12 object-contain"
          layout="desktop"
          alt={intl.formatMessage(
            { id: 'TopbarDesktop.logo' },
            { marketplaceName: config.marketplaceName }
          )}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        <NamedLink name="LandingPage" className="hidden xl:flex items-center no-underline">
          <span className="font-heading font-bold text-xl text-foreground uppercase tracking-wider whitespace-nowrap">
            Construction<span className="text-primary">YardSale</span>.com
          </span>
        </NamedLink>
      </div>

      {searchFormMaybe}

      <CustomLinksMenu
        currentPage={currentPage}
        customLinks={customLinks}
        intl={intl}
        hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
        showCreateListingsLink={showCreateListingsLink}
      />

      <div className="hidden md:flex items-center gap-2 flex-shrink-0">
        {inboxLinkMaybe}
        {authenticatedOnClientSide ? <NotificationBell /> : null}
        {profileMenuMaybe}
        {loginLinkMaybe}
        {signupLinkMaybe}
      </div>
    </div>
  );
};

export default TopbarDesktopCustom;
